import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContainer, Heading, Content, ButtonGroup, Button, Picker, PickerItem, TextField, Checkbox } from '@react-spectrum/s2';
import { toast } from '../../utils/toast';
import { CCOotbModelConfiguration, ModelRegistryType, CCModelUtility, CCSystemDefaults } from '../../types';
import { useProviderMetadata } from '../../hooks/useProviderMetadata';
import { useModelConfigureForm } from '../../hooks/useModelConfigureForm';
import { fetchModelConfigurationValues, createOotbModelConfiguration, updateOotbModelConfiguration, fetchSystemDefaults, setSystemDefaults } from '../../services/api';
import { utilities, labelizeUtilityNames } from '../Models/modelUtilities';
import { DynamicConfigFields } from '../SiteCard/DynamicConfigFields';
import { prepareConfigForSubmit, REDACTED } from '../../utils/secretFields';
import { groupConfigByFieldClass, flattenGroupedConfig } from '../../utils/modelConfig';
import { getModelValueLabel } from '../../constants/modelOptions';
import { ThirdPartyModelDisclaimer } from '../common/ThirdPartyModelDisclaimer';
export interface ConfigureModelDialogProps {
    isOpen: boolean;
    /** Presence = edit mode, following CreateSiteDialog's editSite convention. */ editingConfiguration?: CCOotbModelConfiguration | null;
    onOpenChange: (open: boolean) => void;
    onSaved: () => void;
}
const REGISTRY_TYPES: ModelRegistryType[] = [
    'llm',
    'embedding'
];
export const ConfigureModelDialog: React.FC<ConfigureModelDialogProps> = ({ isOpen, editingConfiguration, onOpenChange, onSaved })=>{
    const { t } = useTranslation();
    const { providers } = useProviderMetadata();
    const isEditMode = !!editingConfiguration;
    const form = useModelConfigureForm(providers);
    const { step, setStep, type, setType, providerKey, setProviderKey, model, setModel, name, setName, description, setDescription, values, setValues, loadedValues, setLoadedValues, dirtyFields, setDirtyFields, isSaving, setIsSaving, configTypeMeta, step2Fields, resetSelection, handleFieldChange, handleFieldDirty, findRegistryEntry } = form;
    // True from the moment an edit dialog opens until fetchModelConfigurationValues
    // resolves. Before that, values/loadedValues are both {}, so
    // prepareConfigForSubmit's isRedacted(loadedValues[field]) check can't see a
    // REDACTED sentinel for any secret field and resolves it to an explicit
    // null instead — which the backend's merge treats as a real overwrite, not
    // "unchanged," wiping the stored credential. Gates Save shut until
    // the real values are in, so that window is never actually reachable.
    const [isLoadingConfigValues, setIsLoadingConfigValues] = useState(false);
    // System-default assignment (Adobe KG AI admin): which utilities this config is the
    // default for. Only meaningful in edit mode (the config must already exist).
    const [defaultUtilities, setDefaultUtilities] = useState<Set<CCModelUtility>>(new Set());
    const [initialDefaultUtilities, setInitialDefaultUtilities] = useState<Set<CCModelUtility>>(new Set());
    const [isLoadingDefaultUtilities, setIsLoadingDefaultUtilities] = useState(false);
    const [defaultUtilitiesError, setDefaultUtilitiesError] = useState<string | null>(null);
    useEffect(()=>{
        if (!isOpen) return;
        let cancelled = false;
        if (editingConfiguration) {
            setDefaultUtilities(new Set());
            setInitialDefaultUtilities(new Set());
            setIsLoadingDefaultUtilities(true);
            setDefaultUtilitiesError(null);
            setType(editingConfiguration.type);
            setProviderKey(editingConfiguration.provider);
            setModel(editingConfiguration.model);
            setName(editingConfiguration.name);
            setDescription(editingConfiguration.description ?? '');
            setValues({});
            setLoadedValues({});
            setDirtyFields(new Set());
            setStep(2);
            setIsLoadingConfigValues(true);
            // Load the real saved values so the form isn't blank on edit — without
            // this, prepareConfigForSubmit would see every field as untouched with
            // no loaded value, which nulls out secrets and drops any non-defaulted
            // behavioral param on save. Secret fields are seeded with the REDACTED
            // sentinel, never the real value (same convention as CreateSiteDialog).
            const providerMeta = providers.find((p)=>p.key === editingConfiguration.provider);
            const fields = providerMeta ? providerMeta[editingConfiguration.type].fields : [];
            fetchModelConfigurationValues(editingConfiguration.id).then((grouped)=>{
                if (cancelled) return; // dialog reopened with a different config
                const loaded = flattenGroupedConfig(grouped);
                const seeded: Record<string, string> = {};
                fields.forEach((field)=>{
                    if (field.name === 'model') return;
                    const raw = loaded[field.name];
                    seeded[field.name] = field.secret ? REDACTED : (raw == null ? '' : String(raw));
                });
                setValues(seeded);
                setLoadedValues(seeded);
                setIsLoadingConfigValues(false);
            }).catch((err: unknown)=>{
                if (cancelled) return;
                toast.negative(err instanceof Error ? err.message : t('commandCenter.configureDialog.failedToLoadValues'));
                setIsLoadingConfigValues(false);
            });
            // Which utilities this config is currently the system default for.
            fetchSystemDefaults().then((entries)=>{
                if (cancelled) return;
                const owned = new Set(entries.filter((e)=>e.configurationId === editingConfiguration.id).map((e)=>e.utility));
                setDefaultUtilities(owned);
                setInitialDefaultUtilities(new Set(owned));
                setIsLoadingDefaultUtilities(false);
            }).catch((err: unknown)=>{
                if (cancelled) return;
                setDefaultUtilitiesError(err instanceof Error ? err.message : t('commandCenter.configureDialog.failedToLoadDefaults'));
                setIsLoadingDefaultUtilities(false);
                toast.negative(err instanceof Error ? err.message : t('commandCenter.configureDialog.failedToLoadDefaults'));
            });
        } else {
            setDefaultUtilities(new Set());
            setInitialDefaultUtilities(new Set());
            setIsLoadingDefaultUtilities(false);
            setDefaultUtilitiesError(null);
            setType('llm');
            setProviderKey(providers[0]?.key ?? '');
            setModel('');
            setName('');
            setDescription('');
            setValues({});
            setLoadedValues({});
            setDirtyFields(new Set());
            setIsLoadingConfigValues(false);
            setStep(1);
        }
        return ()=>{
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen,
        editingConfiguration,
        providers,
        t
    ]);
    const handleClose = useCallback(()=>onOpenChange(false), [
        onOpenChange
    ]);
    const handleSave = useCallback(async ()=>{
        if (!configTypeMeta) return;
        setIsSaving(true);
        try {
            const registryEntry = await findRegistryEntry();
            // Group by field class for the wire. step2Fields excludes `model`
            // (registry_id already carries it), so it never lands in config.
            const flatConfig = prepareConfigForSubmit(step2Fields, values, dirtyFields, loadedValues);
            const config = groupConfigByFieldClass(step2Fields, flatConfig);
            const payload = {
                registryId: registryEntry.id,
                name,
                description: description || undefined,
                config
            };
            if (isEditMode && editingConfiguration) {
                await updateOotbModelConfiguration(editingConfiguration.id, payload);
                // Apply system-default assignment changes (Adobe KG AI admin): only the
                // utilities that actually changed, as a delta (omitted = unchanged).
                const delta: CCSystemDefaults = {};
                utilities(t).filter((u)=>u.type === type).forEach(({ key })=>{
                    const desired = defaultUtilities.has(key);
                    const initial = initialDefaultUtilities.has(key);
                    if (desired && !initial) delta[key] = editingConfiguration.id;
                    else if (!desired && initial) delta[key] = null;
                });
                if (Object.keys(delta).length > 0) {
                    await setSystemDefaults(delta);
                }
                toast.positive(t('commandCenter.configureDialog.updated', {
                    name
                }));
            } else {
                await createOotbModelConfiguration(payload);
                toast.positive(t('commandCenter.configureDialog.configured', {
                    name
                }));
            }
            onSaved();
        } catch (err) {
            // Give backend messages label parity with the UI (e.g. "judge" -> "Evaluation").
            toast.negative(labelizeUtilityNames(err instanceof Error ? err.message : t('commandCenter.configureDialog.failedToSave'), t));
        } finally{
            setIsSaving(false);
        }
    }, [
        configTypeMeta,
        findRegistryEntry,
        step2Fields,
        values,
        dirtyFields,
        loadedValues,
        name,
        description,
        isEditMode,
        editingConfiguration,
        onSaved,
        setIsSaving,
        type,
        defaultUtilities,
        initialDefaultUtilities,
        t
    ]);
    if (!isOpen) return null;
    // A save call in flight locks the whole footer. Cancel/Back stay enabled
    // during isLoadingConfigValues — only Save needs to wait for it (see the
    // flag's declaration for why).
    const busy = isSaving;
    return (<DialogContainer onDismiss={handleClose}>
      <Dialog size="M">
        <Heading slot="title">{isEditMode ? t('commandCenter.configureDialog.editTitle') : t('commandCenter.configureDialog.newTitle')}</Heading>
        <Content>
          {step === 1 ? (<div className={" sd171 _ta171 Ul171 ql171"}>
              <Picker label={t('commandCenter.configureDialog.type')} value={type} onChange={(key)=>{
        setType(key as ModelRegistryType);
        resetSelection();
    }} styles={" Za171"}>
                {REGISTRY_TYPES.map((registryType)=>(<PickerItem key={registryType} id={registryType}>{getModelValueLabel(registryType, t)}</PickerItem>))}
              </Picker>
              <Picker label={t('commandCenter.configureDialog.provider')} value={providerKey} onChange={(key)=>{
        setProviderKey(String(key));
        resetSelection();
    }} styles={" Za171"}>
                {providers.map((p)=>(<PickerItem key={p.key} id={p.key}>{p.label}</PickerItem>))}
              </Picker>
              <Picker label={t('commandCenter.configureDialog.model')} value={model || null} onChange={(key)=>{
        setModel(String(key));
    }} isDisabled={!configTypeMeta?.models.length} styles={" Za171"}>
                {(configTypeMeta?.models ?? []).map((m)=>(<PickerItem key={m} id={m}>{m}</PickerItem>))}
              </Picker>
            </div>) : (<div className={" sd171 _ta171 Ul171 ql171"}>
              <span className="cc-card-desc">
                <strong>{t('commandCenter.configureDialog.providerModel', {
        provider: providers.find((p)=>p.key === providerKey)?.label ?? '',
        model
    })}</strong>
              </span>
              <TextField label={t('commandCenter.configureDialog.configurationName')} value={name} onChange={setName} isRequired styles={" Za171"}/>
              {}
              {configTypeMeta && (<DynamicConfigFields fields={step2Fields} models={configTypeMeta.models} values={values} onFieldChange={handleFieldChange} prefix="ootb" dirtyFields={dirtyFields} onFieldDirty={handleFieldDirty}/>)}
              {isEditMode && (<div className={" sd171 _ta171 Ue171 qe171"}>
                  <span className="cc-card-desc">{t('commandCenter.configureDialog.setAsDefault')}</span>
                  {utilities(t).filter((u)=>u.type === type).map((u)=>(<Checkbox key={u.key} isSelected={defaultUtilities.has(u.key)} onChange={(checked)=>setDefaultUtilities((prev)=>{
                const next = new Set(prev);
                if (checked) next.add(u.key);
                else next.delete(u.key);
                return next;
            })}>
                      {u.label}
                    </Checkbox>))}
                  <span style={{
        fontSize: 13,
        color: 'var(--color-text-secondary, #6b7280)'
    }}>
                    {t('commandCenter.configureDialog.defaultHint')}
                  </span>
                </div>)}
            </div>)}
          <ThirdPartyModelDisclaimer variant="inline"/>
        </Content>
        <ButtonGroup>
          <Button variant="secondary" onPress={handleClose} isDisabled={busy}>{t('primitives.cancel')}</Button>
          {step === 2 && !isEditMode && (<Button variant="secondary" onPress={()=>setStep(1)} isDisabled={busy}>{t('commandCenter.configureDialog.back')}</Button>)}
          {step === 1 && (<Button variant="accent" onPress={()=>setStep(2)} isDisabled={busy || !providerKey || !model}>{t('commandCenter.configureDialog.continue')}</Button>)}
          {step === 2 && (<Button variant="accent" onPress={handleSave} isPending={isSaving} isDisabled={isLoadingConfigValues || isLoadingDefaultUtilities || !!defaultUtilitiesError || !name}>
              {isEditMode ? t('primitives.save') : t('commandCenter.configureDialog.create')}
            </Button>)}
        </ButtonGroup>
      </Dialog>
    </DialogContainer>);
};

import "macro-e77d45646f8bdb461ee9552438da3de2016a7864660e2aa4864c36c173c39db6.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-e77d45646f8bdb461ee9552438da3de2016a7864660e2aa4864c36c173c39db6.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-cd70917bbc5b5938410f1c1ccfc163c6e832ad8bc4e3938f00ea88bd9e68339f.css";